import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Badge, Button, ConnectorIcon, Spinner, Surface } from "@/shared/ui";
import { resolvePreviewCard } from "../lib/previewCards";
import type { ToolState } from "../types";
import { CheckIcon, ChevronDownIcon } from "./icons";
import { PreviewBoundary } from "./previews/PreviewBoundary";

/**
 * The MCP connector tool card. Two shapes:
 *
 *  - Full (a proposal awaiting the user): connector + tool headline,
 *    collapsible JSON arguments, and Approve / Always allow / Decline.
 *    "Always allow" persists server-side — future calls of this tool
 *    auto-approve and render the compact shape straight away.
 *  - Compact (auto-approved or terminal): a status strip — running spinner,
 *    done check, or the failure/decline note. On success the strip becomes a
 *    header for the result, with a locked fallback chain underneath:
 *    typed preview card → collapsible result_preview text → nothing (bare strip).
 */
export function McpToolCard({
  tool,
  onDecision,
}: {
  tool: ToolState;
  onDecision?: (action: "approve" | "always" | "cancel") => void;
}) {
  const { t } = useTranslation();
  const [acted, setActed] = useState(false);
  const [argsOpen, setArgsOpen] = useState(false);
  const [resultOpen, setResultOpen] = useState(false);

  const proposal = tool.proposal;
  const connectorName = proposal?.connector?.name ?? "";
  const connectorSlug = proposal?.connector?.slug ?? null;
  const toolTitle = proposal?.tool_title || proposal?.tool_name || "";
  const args = proposal?.args ?? null;
  const hasArgs = args !== null && Object.keys(args).length > 0;
  const awaiting = tool.status === "proposed" && !tool.autoApproved;

  function decide(action: "approve" | "always" | "cancel") {
    setActed(true);
    onDecision?.(action);
  }

  // Compact strip for everything that needs no decision.
  if (!awaiting) {
    const strip = (
      <div className="flex items-center gap-2.5 rounded-md bg-canvas-soft px-3 py-2">
        <ConnectorIcon
          slug={connectorSlug}
          name={connectorName || toolTitle}
          className="size-4 text-[16px] text-ink-soft"
        />
        <span className="min-w-0 flex-1 truncate font-sans text-[13px] text-ink-soft">
          {t("chat.mcpTool.headline", { tool: toolTitle, connector: connectorName })}
        </span>
        {tool.status === "proposed" || tool.status === "executing" ? (
          <span className="flex shrink-0 items-center gap-1.5 text-ink-muted">
            <Spinner size={13} />
            <span className="font-sans text-[12.5px]">{t("chat.mcpTool.running")}</span>
          </span>
        ) : tool.status === "succeeded" ? (
          <span className="flex shrink-0 items-center gap-1.5 text-ink">
            <CheckIcon className="size-3.5" />
            <span className="font-sans text-[12.5px] font-medium">{t("chat.mcpTool.done")}</span>
          </span>
        ) : tool.status === "declined" ? (
          <span className="shrink-0 font-sans text-[12.5px] text-ink-muted">
            {t("chat.mcpTool.declined")}
          </span>
        ) : tool.status === "expired" ? (
          <span className="shrink-0 font-sans text-[12.5px] text-ink-muted">
            {t("chat.mcpTool.expired")}
          </span>
        ) : (
          <span
            className="max-w-[45%] shrink-0 truncate font-sans text-[12.5px] text-ink-muted"
            title={tool.error ?? undefined}
          >
            {tool.error || t("chat.mcpTool.failed")}
          </span>
        )}
      </div>
    );

    // Locked fallback chain under the success strip: typed card →
    // result_preview text → bare strip. The preview payload is third-party
    // data — validation failures and render crashes degrade, never throw.
    const typed =
      tool.status === "succeeded" && tool.preview ? resolvePreviewCard(tool.preview) : null;
    const TypedCard = typed?.Component ?? null;
    const textBlock =
      tool.status === "succeeded" && tool.resultPreview ? (
        <div className="flex flex-col gap-1.5">
          <button
            type="button"
            onClick={() => setResultOpen((on) => !on)}
            aria-expanded={resultOpen}
            className="flex items-center gap-1 self-start font-sans text-[12.5px] font-medium text-ink outline-none transition-colors duration-150 ease-house hover:text-ink focus-visible:underline"
          >
            <ChevronDownIcon
              className={
                resultOpen ? "size-3.5 rotate-180 transition-transform" : "size-3.5 transition-transform"
              }
            />
            {t("chat.mcpTool.result")}
          </button>
          {resultOpen ? (
            <pre className="max-h-48 overflow-auto whitespace-pre-wrap rounded-md bg-canvas-soft p-3 font-mono text-[12px] leading-[1.5] text-ink-soft">
              {tool.resultPreview}
            </pre>
          ) : null}
        </div>
      ) : null;

    if (!TypedCard && !textBlock) return <div className="my-2">{strip}</div>;
    return (
      <div className="my-2 flex flex-col gap-2">
        {strip}
        {typed && TypedCard ? (
          <PreviewBoundary fallback={textBlock}>
            <div className="overflow-hidden rounded-md bg-canvas-soft">
              <TypedCard data={typed.data} connectorName={connectorName} />
            </div>
          </PreviewBoundary>
        ) : (
          textBlock
        )}
      </div>
    );
  }

  return (
    <Surface
      tone="panel-2"
      border="none"
      radius="md"
      shadow="soft"
      className="my-3 flex flex-col gap-3 p-4"
    >
      <div className="flex items-center gap-3">
        <span className="grid size-9 shrink-0 place-items-center rounded-md bg-bubble text-ink">
          <ConnectorIcon
            slug={connectorSlug}
            name={connectorName || toolTitle}
            className="size-[18px] text-[18px]"
          />
        </span>
        <span className="min-w-0 flex-1 font-serif text-[16px] font-medium text-ink">
          {t("chat.mcpTool.headline", { tool: toolTitle, connector: connectorName })}
        </span>
        {proposal?.read_only ? (
          <Badge tone="soft" size="sm">
            {t("chat.mcpTool.readOnly")}
          </Badge>
        ) : null}
      </div>

      {hasArgs ? (
        <div className="flex flex-col gap-1.5">
          <button
            type="button"
            onClick={() => setArgsOpen((on) => !on)}
            aria-expanded={argsOpen}
            aria-label={t("chat.mcpTool.argumentsAria")}
            className="flex items-center gap-1 self-start font-sans text-[12.5px] font-medium text-ink outline-none transition-colors duration-150 ease-house hover:text-ink focus-visible:underline"
          >
            <ChevronDownIcon
              className={argsOpen ? "size-3.5 rotate-180 transition-transform" : "size-3.5 transition-transform"}
            />
            {t("chat.mcpTool.arguments")}
          </button>
          {argsOpen ? (
            <pre className="max-h-48 overflow-auto rounded-md bg-canvas-soft p-3 font-mono text-[12px] leading-[1.5] text-ink-soft">
              {JSON.stringify(args, null, 2)}
            </pre>
          ) : null}
        </div>
      ) : null}

      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="ghost" disabled={acted} onClick={() => decide("always")}>
          {t("chat.mcpTool.alwaysAllow")}
        </Button>
        <Button size="sm" variant="ink" disabled={acted} onClick={() => decide("approve")}>
          {t("chat.mcpTool.approve")}
        </Button>
        <Button size="sm" variant="subtle" disabled={acted} onClick={() => decide("cancel")}>
          {t("chat.mcpTool.decline")}
        </Button>
      </div>
    </Surface>
  );
}
